/**
 * Anbindung der Blog-Dateien an die Anwendung.
 *
 * Alles Unreine sitzt hier: der Vite-Glob über `content/blog`. Die Parser in
 * `content.ts` und `tools.ts` bleiben dadurch frei von Datei-Zugriff und sind
 * unter Vitest testbar (docs/TESTING_GUIDE.md).
 *
 * Ein neuer Artikel ist EINE Datei — `content/blog/<slug>.md` mit Frontmatter.
 * Kein Eintrag in einer Liste, keine neue Komponente: `src/prerender-public.tsx`
 * leitet die vorzurendernden Routen aus `BLOG_ARTICLES` ab.
 */
import { parseBlogArticle, type BlogArticle } from "./content";
import { blogArticlePath, BLOG_INDEX_PATH } from "./routes";
import { parseToolComparison, type ToolComparison } from "./tools";

const articleSources = import.meta.glob("/content/blog/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

const comparisonSources = import.meta.glob("/content/blog/tools-vergleich.json", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

function slugFromPath(filePath: string): string {
  return filePath.split("/").pop()?.replace(/\.md$/i, "") ?? "";
}

/**
 * Alle Artikel, neueste zuerst. Enthält auch Entwürfe — welche davon sichtbar
 * sind, entscheidet der jeweilige Aufrufer über `status`.
 */
export const BLOG_ARTICLES: BlogArticle[] = Object.entries(articleSources)
  .map(([filePath, raw]) => parseBlogArticle(raw, slugFromPath(filePath)))
  .filter((article) => article.frontmatter.slug && article.title)
  .sort((a, b) => b.frontmatter.published.localeCompare(a.frontmatter.published));

/** Artikel, die auf der Übersicht erscheinen und in die Sitemap gehören. */
export const PUBLISHED_BLOG_ARTICLES: BlogArticle[] = BLOG_ARTICLES.filter(
  (article) => article.frontmatter.status === "published",
);

export function getBlogArticle(slug: string): BlogArticle | null {
  const wanted = String(slug ?? "").trim().toLowerCase();
  return BLOG_ARTICLES.find((a) => a.frontmatter.slug.toLowerCase() === wanted) ?? null;
}

/** Daten der Vergleichstabelle (Slot `[[slot:tools-table]]`). */
export const TOOL_COMPARISON: ToolComparison = parseToolComparison(
  Object.values(comparisonSources)[0] ?? "",
);

// Weiterreichen, damit Aufrufer nicht zwei Module importieren müssen.
export { blogArticlePath, BLOG_INDEX_PATH };
